import React, { forwardRef, memo, ComponentPropsWithRef } from 'react'

import InputMask from '@mona-health/react-input-mask'

import { Input } from './Input'

/**
 * E.164 is the international telephone numbering plan that
 * ensures each device on the PSTN has globally unique number.
 *
 * This number allows phone calls and text messages can be
 * correctly routed to individual phones in different countries.
 * E.164 numbers are formatted [+] [country code] [subscriber number including area code]
 * and can have a maximum of fifteen digits.
 *
 * Regex is ^\+[1-9]\d{1,14}$
 */
export const DEFAULT_US_FORMAT = '+1 999-999-9999'

type InputProps = ComponentPropsWithRef<typeof Input>

interface PhoneNumberInputProps extends InputProps {
  /**
   * Whether mask prefix and placeholder should be displayed when input is empty and has no focus
   *
   * Default is false
   */
  alwaysShowMask?: boolean
  /**
   * Mask used to mask phone number, default is international format for US
   *
   * If you change mask, please unset maxLength or set it to new length instead
   */
  mask?: string | (string | RegExp)[]
  /**
   * Placeholder for mask, default is null
   */
  maskPlaceholder?: string | null
}

export const PhoneNumberInput = memo(
  forwardRef<HTMLInputElement, PhoneNumberInputProps>(
    ({ mask = DEFAULT_US_FORMAT, maskPlaceholder = null, ...rest }, ref) => (
      <Input
        {...{
          as: InputMask,
          autoComplete: 'off',
          mask,
          maskPlaceholder,
          ref,
          type: 'tel',
        }}
        {...rest}
      />
    ),
  ),
)
